import React, { useEffect, useRef, useState } from "react";
import { api, errMsg, srcForCover } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasAccess, formatDate } from "@/lib/utils2";
import PageHeader from "@/components/PageHeader";
import EpisodeRow from "@/components/EpisodeRow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Upload, Eye, EyeOff, Trash2, Pencil, Loader2, CheckCircle2 } from "lucide-react";

const HOSTS = ["Elias", "Maya"];

function EpisodeForm({ episode, onDone }) {
  const isEdit = !!episode;
  const [form, setForm] = useState({
    title: episode?.title || "",
    description: episode?.description || "",
    hosts: episode?.hosts || [],
    season: episode?.season || "",
    episode_number: episode?.episode_number || "",
    tags: (episode?.tags || []).join(", "),
    publish_now: true,
    publish_at: "",
  });
  const [audio, setAudio] = useState(null); // {path, filename}
  const [cover, setCover] = useState(episode?.cover_image_path ? { path: episode.cover_image_path } : null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const audioInput = useRef();
  const coverInput = useRef();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const toggleHost = (h) => {
    setForm((f) => ({ ...f, hosts: f.hosts.includes(h) ? f.hosts.filter((x) => x !== h) : [...f.hosts, h] }));
  };

  const uploadFile = async (file, kind, setter) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter({ path: data.path, filename: data.filename });
      toast.success(`${kind === "audio" ? "Audio" : "Cover"} uploaded`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!isEdit && !audio) return toast.error("Please upload an .m4a audio file.");
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        hosts: form.hosts,
        season: form.season ? Number(form.season) : null,
        episode_number: form.episode_number ? Number(form.episode_number) : null,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (isEdit) {
        await api.put(`/episodes/${episode.id}`, payload);
      } else {
        payload.audio_path = audio.path;
        payload.audio_filename = audio.filename;
        payload.cover_image_path = cover?.path || null;
        payload.publish_now = form.publish_now;
        payload.publish_at = form.publish_now ? null : new Date(form.publish_at).toISOString();
        await api.post("/episodes", payload);
      }
      toast.success(isEdit ? "Episode updated" : "Episode created");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto scrollbar-thin pr-1">
      {!isEdit && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Audio file (.m4a)</Label>
            <input ref={audioInput} type="file" accept=".m4a,audio/*" hidden data-testid="episode-audio-input"
              onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "audio", setAudio)} />
            <Button type="button" variant="outline" onClick={() => audioInput.current.click()} className="mt-1.5 w-full rounded-md border-brand-line justify-start" data-testid="episode-audio-button">
              {audio ? <CheckCircle2 className="w-4 h-4 mr-2 text-brand" /> : <Upload className="w-4 h-4 mr-2" />}
              {audio ? audio.filename : "Upload audio"}
            </Button>
          </div>
          <div>
            <Label>Cover image (optional)</Label>
            <input ref={coverInput} type="file" accept="image/*" hidden
              onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], "cover", setCover)} />
            <Button type="button" variant="outline" onClick={() => coverInput.current.click()} className="mt-1.5 w-full rounded-md border-brand-line justify-start">
              {cover ? <CheckCircle2 className="w-4 h-4 mr-2 text-brand" /> : <Upload className="w-4 h-4 mr-2" />}
              {cover ? "Cover ready" : "Upload cover"}
            </Button>
          </div>
        </div>
      )}
      <div>
        <Label>Title</Label>
        <Input data-testid="episode-title" value={form.title} onChange={set("title")} className="mt-1.5 border-brand-line" />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea data-testid="episode-description" value={form.description} onChange={set("description")} rows={3} className="mt-1.5 border-brand-line" />
      </div>
      <div>
        <Label>Hosts</Label>
        <div className="flex gap-2 mt-1.5">
          {HOSTS.map((h) => (
            <button key={h} type="button" onClick={() => toggleHost(h)} data-testid={`host-${h}`}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors duration-200 ${form.hosts.includes(h) ? "bg-brand text-white border-brand" : "border-brand-line text-brand-ink"}`}>
              {h}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div><Label>Season</Label><Input value={form.season} onChange={set("season")} type="number" className="mt-1.5 border-brand-line" /></div>
        <div><Label>Episode #</Label><Input value={form.episode_number} onChange={set("episode_number")} type="number" className="mt-1.5 border-brand-line" /></div>
        <div><Label>Tags (comma)</Label><Input value={form.tags} onChange={set("tags")} className="mt-1.5 border-brand-line" /></div>
      </div>
      {!isEdit && (
        <div className="rounded-xl border border-brand-line p-4 bg-brand-sidebar/50">
          <div className="flex items-center justify-between">
            <div>
              <Label>Publish immediately</Label>
              <p className="text-xs text-brand-muted mt-0.5">Turn off to schedule a future release.</p>
            </div>
            <Switch checked={form.publish_now} onCheckedChange={(v) => setForm({ ...form, publish_now: v })} data-testid="publish-now-switch" />
          </div>
          {!form.publish_now && (
            <div className="mt-3">
              <Label>Release date &amp; time</Label>
              <Input type="datetime-local" value={form.publish_at} onChange={set("publish_at")} className="mt-1.5 border-brand-line bg-white" data-testid="schedule-input" />
            </div>
          )}
        </div>
      )}
      <DialogFooter>
        <Button onClick={save} disabled={saving || uploading} className="rounded-full brand-btn" data-testid="save-episode-button">
          {(saving || uploading) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {isEdit ? "Save changes" : "Create episode"}
        </Button>
      </DialogFooter>
    </div>
  );
}

const STATUS_BADGE = {
  published: "bg-brand/15 text-brand",
  scheduled: "bg-brand-dough/25 text-brand-ink",
  hidden: "bg-brand-muted/15 text-brand-muted",
  draft: "bg-brand-muted/15 text-brand-muted",
};

export default function Podcasts() {
  const { user } = useAuth();
  const isCreator = hasAccess(user, "podcast_creator");
  const [episodes, setEpisodes] = useState([]);
  const [manage, setManage] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadUser = async () => {
    const { data } = await api.get("/episodes");
    setEpisodes(data.episodes);
  };
  const loadManage = async () => {
    if (!isCreator) return;
    const { data } = await api.get("/episodes/manage");
    setManage(data.episodes);
  };

  useEffect(() => { loadUser(); loadManage(); /* eslint-disable-next-line */ }, []);

  const setStatus = async (ep, status) => {
    try {
      await api.put(`/episodes/${ep.id}`, { status });
      toast.success(`Episode ${status === "hidden" ? "hidden" : "published"}`);
      loadUser(); loadManage();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (ep) => {
    try {
      await api.delete(`/episodes/${ep.id}`);
      toast.success("Episode removed");
      loadUser(); loadManage();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const UserView = (
    <div className="rounded-2xl border border-brand-line bg-brand-sidebar/40 p-2 sm:p-3" data-testid="podcast-user-list">
      {episodes.length === 0 && <p className="text-brand-muted text-sm p-6 text-center">No episodes available yet.</p>}
      {episodes.map((ep) => <EpisodeRow key={ep.id} episode={ep} onFavoriteToggle={loadUser} />)}
    </div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl">
      <PageHeader title="Podcasts" subtitle="Coaching episodes from Elias and Maya. Favorites pin to the top; new episodes are flagged until you listen.">
        {isCreator && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full brand-btn" data-testid="new-episode-button"><Plus className="w-4 h-4 mr-2" /> New episode</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl border-brand-line">
              <DialogHeader><DialogTitle className="font-display text-2xl">Upload episode</DialogTitle></DialogHeader>
              <EpisodeForm onDone={() => { setCreateOpen(false); loadUser(); loadManage(); }} />
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {isCreator ? (
        <Tabs defaultValue="listen">
          <TabsList className="bg-brand-sidebar h-auto p-1 rounded-full mb-6">
            <TabsTrigger value="listen" data-testid="tab-listen" className="rounded-full data-[state=active]:bg-white px-5 py-2">Listen</TabsTrigger>
            <TabsTrigger value="manage" data-testid="tab-manage" className="rounded-full data-[state=active]:bg-white px-5 py-2">Manage</TabsTrigger>
          </TabsList>
          <TabsContent value="listen">{UserView}</TabsContent>
          <TabsContent value="manage">
            <div className="space-y-3" data-testid="podcast-manage-list">
              {manage.map((ep) => (
                <div key={ep.id} className="rounded-xl border border-brand-line bg-white p-4 flex items-center gap-4">
                  <img src={srcForCover(ep)} alt="" className="h-12 w-12 rounded-lg object-cover border border-brand-line" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-brand-ink truncate">{ep.title}</p>
                      <Badge className={`rounded-full border-0 ${STATUS_BADGE[ep.status] || ""}`}>{ep.status}</Badge>
                    </div>
                    <p className="text-sm text-brand-muted">{ep.hosts?.join(" & ")} · {formatDate(ep.publish_at)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Dialog open={editing?.id === ep.id} onOpenChange={(o) => setEditing(o ? ep : null)}>
                      <DialogTrigger asChild>
                        <Button size="icon" variant="ghost" data-testid={`edit-episode-${ep.id}`}><Pencil className="w-4 h-4" /></Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl border-brand-line">
                        <DialogHeader><DialogTitle className="font-display text-2xl">Edit episode</DialogTitle></DialogHeader>
                        <EpisodeForm episode={ep} onDone={() => { setEditing(null); loadUser(); loadManage(); }} />
                      </DialogContent>
                    </Dialog>
                    {ep.status === "hidden" ? (
                      <Button size="icon" variant="ghost" onClick={() => setStatus(ep, "published")} data-testid={`show-episode-${ep.id}`} title="Publish"><Eye className="w-4 h-4" /></Button>
                    ) : (
                      <Button size="icon" variant="ghost" onClick={() => setStatus(ep, "hidden")} data-testid={`hide-episode-${ep.id}`} title="Hide"><EyeOff className="w-4 h-4" /></Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => remove(ep)} className="text-brand-terracotta" data-testid={`remove-episode-${ep.id}`} title="Remove"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
              {manage.length === 0 && <p className="text-brand-muted text-sm p-6 text-center">No episodes yet. Upload your first one.</p>}
            </div>
          </TabsContent>
        </Tabs>
      ) : UserView}
    </div>
  );
}
